import { useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ConfirmDialog } from "./ConfirmDialog";
import { Field, SettingsSection } from "./SettingsSection";

const EMPTY = { current_password: "", new_password: "", confirm_password: "" };

export function SecurityCard() {
  const { clear } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof EMPTY) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });
  const tooShort = form.new_password.length > 0 && form.new_password.length < 8;
  const mismatch = form.confirm_password.length > 0 && form.new_password !== form.confirm_password;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post<{ closed_sessions: number }>("/settings/password", form);
      setForm(EMPTY);
      toast.success(`Contraseña actualizada. Cerramos ${r.data.closed_sessions} sesión(es) en otros dispositivos.`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const logoutAll = async () => {
    try {
      await api.post("/settings/logout-all");
      clear();
      navigate("/login", { replace: true });
      toast.success("Cerramos tu sesión en todos tus dispositivos");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const pwd = (k: keyof typeof EMPTY, label: string, hint?: string) => (
    <Field label={label} htmlFor={`settings-${k}`} hint={hint}>
      <Input id={`settings-${k}`} type="password" autoComplete={k === "current_password" ? "current-password" : "new-password"}
        value={form[k]} onChange={set(k)} className="h-11" data-testid={`settings-${k.replace(/_/g, "-")}-input`} />
    </Field>
  );

  return (
    <SettingsSection id="seguridad" icon={ShieldCheck} title="Seguridad" desc="Cambia tu contraseña o cierra tu sesión en todos lados.">
      <form onSubmit={submit} className="grid gap-5 sm:grid-cols-3" data-testid="settings-password-form">
        {pwd("current_password", "Contraseña actual")}
        {pwd("new_password", "Nueva contraseña", "Mínimo 8 caracteres")}
        {pwd("confirm_password", "Repite la nueva")}
        {(tooShort || mismatch) && (
          <p className="text-sm text-rose-600 sm:col-span-3" data-testid="settings-password-error">
            {tooShort ? "La nueva contraseña debe tener al menos 8 caracteres." : "Las contraseñas nuevas no coinciden."}
          </p>
        )}
        <div className="flex flex-col gap-3 sm:col-span-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-500">Al cambiarla cerramos tus demás sesiones; esta sigue abierta.</p>
          <Button type="submit" disabled={saving || tooShort || mismatch || !form.current_password || !form.new_password || !form.confirm_password}
            className="h-11 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="settings-password-submit-button">
            <KeyRound className="mr-2 h-4 w-4" /> {saving ? "Cambiando…" : "Cambiar contraseña"}
          </Button>
        </div>
      </form>
      <div className="mt-7 flex flex-col gap-3 border-t border-[#DDE1E8] pt-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800">Cerrar sesión en todos mis dispositivos</p>
          <p className="text-xs text-slate-500">Incluye este. Tendrás que volver a entrar.</p>
        </div>
        <ConfirmDialog testId="logout-all" title="¿Cerrar sesión en todos tus dispositivos?" confirmLabel="Cerrar todas"
          description="Cerraremos tu sesión en celulares, computadoras y en este navegador." onConfirm={logoutAll}
          trigger={<Button variant="outline" className="h-11 border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="settings-logout-all-button"><LogOut className="mr-2 h-4 w-4" /> Cerrar sesión en todos</Button>} />
      </div>
    </SettingsSection>
  );
}
